import { CREW } from '../constants';
import { addNotification, removeNotification } from '../actions/notification';
import { getHostessCost } from './pricing';
import { updateSearch } from './querystring';
import history from '../history';
import { getYachtLayoutSpots } from './ratings';
import { yachtCapacityWithHost } from './yachts';
import { COUNTRY, getCountryCode } from './country';

/**
 * Most guests a yacht may take: 12 in Greece, 14 elsewhere. Pass the destination or week, in any payload
 * shape (see getCountryCode), so every Greek destination is capped, festival destinations included.
 *
 * @param {object|null|undefined} destination
 */
export const absoluteMaxCapacityForDestination = destination => (getCountryCode(destination) === COUNTRY.GREECE ? 12 : 14);

export const maxBoatCapacityByDestination = (boatCapacity, destination) => (
  absoluteMaxCapacityForDestination(destination) > boatCapacity ? boatCapacity : absoluteMaxCapacityForDestination(destination)
);

export const handleCrewChange = (boys, girls, operator, min = 0, max) => {
  const total = girls + boys;
  const newTotal = total + operator;
  if (newTotal < min || (newTotal > max && operator === 1)) return null;
  const newCrew = {
    boys: Math.floor(newTotal / 2),
    girls: Math.ceil(newTotal / 2)
  };
  if (total !== newTotal) {
    history.replace({
      pathname: history.location.pathname,
      search: updateSearch(history.location, {
        ...newCrew
      })
    });
  }
};
export const handleHostChange = (crew, operator, name, min = 0, max) => {
  const newValue = crew[name] + operator;
  if (newValue < min || (newValue > max && operator === 1)) return null;
  if (crew[name] !== newValue) {
    history.replace({
      pathname: history.location.pathname,
      search: updateSearch(history.location, { [name]: newValue })
    });
  }
};
export const updateCrewHistory = props => history.replace({
  pathname: props.location.pathname,
  search: updateSearch(props.location, props.crew)
});

export const hostessAvailableCheckSelectedYacht = hostessCost => hostessCost > 0;

export const handleCrewNotifications = (destinations, location, crew, capacity, maxGuests, ratio, noHostessSpace, dispatch, week, destinationId, yacht) => {
  const total = crew.total;
  const isUnderRatio = crew.ratio < ratio;
  const guests = crew.boys + crew.girls;

  const yachtLayoutSpots = getYachtLayoutSpots(destinations, location, yacht);
  const { capacity: capacityWithHost, maxGuests: maxGuestsWithHost } = yachtCapacityWithHost(yachtLayoutSpots, maxGuests, capacity, destinationId); // host is in some cases taking 2 spots
  if (crew.hostess) {
    maxGuests = maxGuestsWithHost;
    capacity = capacityWithHost;
  }
  // the week knows its country; otherwise take the destination from the list
  const capacitySource = getCountryCode(week) ? week : (destinations || []).find(d => d.id === destinationId);
  const absoluteMaxCapacity = absoluteMaxCapacityForDestination(capacitySource);
  const maxCapacity = Math.min(capacity, absoluteMaxCapacity);

  if (guests > maxGuests || total > maxCapacity) {
    dispatch(addNotification({
      uid: 11,
      type: 'error',
      priority: 10,
      title: '👨‍👩‍👧‍👦 Decrease your crew size',
      text: `Woah. Our yachts are big, but not that big.
        Please fill in your crew number to maximum of ${maxCapacity} people (max ${maxGuests} guests).`
    }));
  } else {
    dispatch(removeNotification(11));
  }

  if (isUnderRatio) {
    dispatch(addNotification({
      uid: 1,
      type: 'error',
      priority: 10,
      title: 'Incorrect gender ratio.',
      text: 'No more guy spots, please get in touch with customer services.'
      // cta: {
      //   link: '/account/crew-finder',
      //   description: 'Pro tip: Can\'t achieve the ratio? Try adding a host or try our',
      //   text: 'Crew finder',
      //   external: true,
      // },
    }));
  } else if (noHostessSpace && week) {
    dispatch(addNotification({
      uid: 2,
      type: 'warning',
      priority: 2,
      title: 'Host',
      text: `Your yacht is already at max capacity, adding a host will mean sacrificing filled spots on your yacht.  Hosts are €${getHostessCost({ week }, crew, true)} and totally worth it.`
    }));
  } else {
    dispatch(removeNotification(2));
  }
};

/**
 * Returns 'fake' crew object simulating yacht filled to max capacity with guests (no host)
 *
 * @param boatCapacity int
 * @returns {{skipper: number, hostess: number, total: number, boys: number, girls: number, ratio: number}}
 */
export const maxCapacityCrew = boatCapacity => {
  const maxGuests = boatCapacity - 1 > CREW.MAX_GUESTS ? CREW.MAX_GUESTS : boatCapacity - 1;
  const girls = Math.ceil(maxGuests / 2);
  const boys = maxGuests - girls;
  const ratio = Math.round((girls / maxGuests) * 100);
  return {
    boys,
    girls,
    skipper: 1,
    hostess: 0,
    total: boys + girls + 1,
    ratio
  };
};

export const calculateRatio = crew => {
  const {
    girls, hostess, skipper, total
  } = crew;
  return Math.round(((girls + (hostess || 0)) / (total - (skipper || 1))) * 100) || 0;
};

export const getProfileImage = (fbUid, profilePicture, name) => {
  if (profilePicture) {
    return {
      url: profilePicture,
      alt: name
    };
  }
  if (fbUid) {
    return {
      url: `https://graph.facebook.com/v2.9/${fbUid}/picture?type=square`,
      alt: name
    };
  }
  return undefined;
};
